import { Helmet } from 'react-helmet-async';
import { filter } from 'lodash';
import { sentenceCase } from 'change-case';
import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';

// @mui
import {
  Card,
  Table,
  Stack,
  Paper,
  Avatar,
  Checkbox,
  TableRow,
  TableBody,
  TableCell,
  Typography,
  IconButton,
  TableContainer,
  TablePagination,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { jwtDecode } from 'jwt-decode';
import { API_BASE_URL } from '../../utils/env';

// components
import Iconify from '../iconify';
import Scrollbar from '../scrollbar';

// sections
import { UserListHead } from '../../sections/@dashboard/user';


const TABLE_HEAD = [
  { id: 'name', label: 'Name', alignRight: false },
  { id: 'gener', label: 'Gender', alignRight: false },
  { id: 'type', label: 'Type', alignRight: false },
  { id: 'department', label: 'Department', alignRight: false },
  { id: 'time', label: 'Time', alignRight: false },
  { id: '' },
];

// ----------------------------------------------------------------------

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function applySortFilter(array, comparator, query) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  if (query) {
    return filter(array, (_user) => _user.lastName.toLowerCase().indexOf(query.toLowerCase()) !== -1);
  }
  return stabilizedThis.map((el) => el[0]);
}

export default function Attendance() {
  const [open, setOpen] = useState(null);
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('asc');
  const [selected, setSelected] = useState([]);
  const [orderBy, setOrderBy] = useState('name');
  const [filterName, setFilterName] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [attendance, setAttendance] = useState([]);
  const [attendanceError, setAttendanceError] = useState(null);
  const [attendanceLoader, setAttendanceLoader] = useState(false);

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // get the token
  const { userInfo } = useSelector((state) => state.auth);
  const decodedToken = jwtDecode(userInfo.token);
  const parishId = decodedToken.parishId;

  // token to be used in headers for the token-based authentication
  const token = userInfo.token;

  useEffect(() => {
    const fetchAttendance = async () => {
      setAttendanceLoader(true);
      const currentDate = new Date().toISOString().split('T')[0];

      const requestBody = {
        parishId,
        startDate: startDate || currentDate,
        endDate: endDate || currentDate,
        page: page + 1,
        pageSize: rowsPerPage,
      };

      try {
        const response = await fetch(`${API_BASE_URL}/Attendance/GetAttendance`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody),
        });

        if (!response.ok) {
          const errorResponse = await response.json();
          throw new Error(errorResponse.error || 'Failed to fetch attendance');
        }

        const responseData = await response.json();

        if (responseData.success && responseData.data === null) {
          setAttendance([]);
          setAttendanceError(responseData.message);
        } else {
          setAttendance(responseData.data);
          setAttendanceError(null);
        }
      } catch (error) {
        setAttendanceError(error.message);
      } finally {
        setAttendanceLoader(false);
      }
    };

    if (parishId && token) {
      fetchAttendance();
    }
  }, [parishId, token, startDate, endDate]);


  const USERLIST = attendance;

  const handleOpenMenu = (event) => {
    setOpen(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setOpen(null);
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = USERLIST.map((n) => n.name);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, name) => {
    const selectedIndex = selected.indexOf(name);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
    }
    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setPage(0);
    setRowsPerPage(parseInt(event.target.value, 10));
  };

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - USERLIST.length) : 0;

  const filteredUsers = applySortFilter(USERLIST, getComparator(order, orderBy), filterName);

  const isNotFound = !filteredUsers.length && !!filterName;

  const formatDate = (date) => {
    const options = { day: 'numeric', month: 'short', year: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  };

  const currentDate = new Date();
  const formattedDate = formatDate(currentDate);

  return (
    <>
      <Helmet>
        <title> Attendance | CaaS </title>
      </Helmet>

      <Card style={{ background: '#fcfcfc' }}>
        <Typography fontSize={18} fontWeight={600} color="#808191" padding={3}>
          Attendees for {formattedDate}
        </Typography>

        <Scrollbar>
          <TableContainer sx={{ minWidth: 800 }} style={{ background: '#fcfcfc' }}>
            <Table>
              <UserListHead
                order={order}
                orderBy={orderBy}
                headLabel={TABLE_HEAD}
                rowCount={USERLIST.length}
                numSelected={selected.length}
                onRequestSort={handleRequestSort}
                onSelectAllClick={handleSelectAllClick}
              />
              <TableBody>
                {attendance.length === 0 ? (
                  <TableRow>
                    <TableCell align="center" colSpan={7} sx={{ py: 3 }}>
                      <Paper
                        sx={{
                          textAlign: 'center',
                        }}
                      >
                        <Typography variant="h6" paragraph color={'#585858'}>
                          Message:
                        </Typography>

                        <Typography variant="body2" color={'#808191'}>
                          No attendance taken at this time
                        </Typography>
                      </Paper>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => {
                    const {
                      memberId,
                      firstName,
                      lastName,
                      department,
                      gender,
                      memberType,
                      phoneNumber,
                      attendanceTime,
                    } = row;
                    const selectedUser = selected.indexOf(firstName) !== -1;

                    // Format attendance time to 12-hour format
                    const formattedTime = new Date(attendanceTime).toLocaleTimeString('en-US', {
                      hour: 'numeric',
                      minute: 'numeric',
                      hour12: true,
                    });

                    return (
                      <TableRow hover key={memberId} tabIndex={-1} role="checkbox" selected={selectedUser}>
                        <TableCell padding="checkbox" style={{ visibility: 'hidden' }}>
                          <Checkbox checked={selectedUser} onChange={(event) => handleClick(event, firstName)} />
                        </TableCell>

                        <TableCell component="th" scope="row" padding="none">
                          <Stack direction="row" alignItems="center" spacing={2}>
                            <Avatar alt={lastName} src={'../lazyPath'} />

                            <div className="wrap">
                              <div className="name">
                                <Typography variant="subtitle2" noWrap>
                                  {`${lastName} ${firstName}`}
                                </Typography>
                              </div>

                              <div className="phone">
                                <Typography variant="subtitle6" noWrap>
                                  {phoneNumber}
                                </Typography>
                              </div>
                            </div>
                          </Stack>
                        </TableCell>

                        <TableCell align="left">{gender}</TableCell>

                        <TableCell align="left">{memberType}</TableCell>

                        <TableCell align="left">{department}</TableCell>

                        <TableCell align="left">{formattedTime}</TableCell>

                        <TableCell align="right" style={{ visibility: 'hidden' }}>
                          <IconButton
                            size="large"
                            color="inherit"
                          // onClick={(event) => {
                          //   setSelectedUserId(memberId);
                          //   handleOpenMenu(event);
                          // }}
                          >
                            <Iconify icon={'emojione-v1:left-check-mark'} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
                {emptyRows > 0 && (
                  <TableRow style={{ height: 53 * emptyRows }}>
                    <TableCell colSpan={6} />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Scrollbar>

        <TablePagination
          rowsPerPageOptions={[10, 20, 35]}
          component="div"
          count={USERLIST.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
        />
      </Card>
    </>
  );
}
